/**
 * OpsGate — password protection wrapper for /ops.
 *
 * Renders a lock-screen with a single password field. On success stores the
 * bearer token in localStorage and mounts the children (the actual Ops UI).
 */
import React from "react";
import axios from "axios";
import { Link } from "react-router-dom";
import { Lock, LogIn, ChevronRight, ShieldCheck } from "lucide-react";
import { getOpsToken, setOpsToken, clearOpsToken } from "@/lib/opsAuth";

const API = `${(process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "")}/api`;

// Install an axios request interceptor once — attaches the Ops bearer token
// to every /api/ops/* call automatically. Idempotent: guarded by a flag on
// the axios instance so multiple mounts don't stack interceptors.
if (!axios.__ops_interceptor_installed__) {
  axios.__ops_interceptor_installed__ = true;
  axios.interceptors.request.use((config) => {
    try {
      const url = String(config?.url || "");
      if (url.includes("/api/ops/") && !url.includes("/api/ops/auth/verify")) {
        const t = getOpsToken();
        if (t) {
          config.headers = { ...(config.headers || {}), Authorization: `Bearer ${t}` };
        }
      }
    } catch (_) { /* ignore */ }
    return config;
  });
  // On 401 responses from /ops routes, clear the stored token so the gate re-appears.
  axios.interceptors.response.use(
    (r) => r,
    (err) => {
      try {
        const url = String(err?.config?.url || "");
        if (err?.response?.status === 401 && url.includes("/api/ops/")) {
          clearOpsToken();
          if (typeof window !== "undefined" && window.location?.pathname?.startsWith("/ops")) {
            window.location.reload();
          }
        }
      } catch (_) { /* ignore */ }
      return Promise.reject(err);
    }
  );
}

const P = {
  bg: "#080B12", bg2: "#0A0F1A", border: "rgba(255,255,255,0.08)", surface: "rgba(255,255,255,0.03)",
  ink: "#E8ECF0", dim: "rgba(232,236,240,0.68)", silver: "#C0C7CE", green: "#22C55E", rose: "#F43F5E",
};

export default function OpsGate({ children }) {
  const [authed, setAuthed] = React.useState(() => !!getOpsToken());
  const [password, setPassword] = React.useState("");
  const [loading, setLoading] = React.useState(false);
  const [error, setError] = React.useState("");
  const inputRef = React.useRef(null);

  // Validate token freshness on mount
  React.useEffect(() => {
    if (!authed) return;
    axios.get(`${API}/ops/auth/whoami`, {
      headers: { Authorization: `Bearer ${getOpsToken()}` },
    }).catch(() => {
      clearOpsToken();
      setAuthed(false);
    });
  }, [authed]);

  const submit = async (e) => {
    e?.preventDefault?.();
    if (!password) return;
    setLoading(true);
    setError("");
    try {
      const { data } = await axios.post(`${API}/ops/auth/verify`, { password });
      if (data?.token) {
        setOpsToken(data.token, data.expires_at);
        setAuthed(true);
        setPassword("");
      } else {
        setError("Unexpected response");
      }
    } catch (err) {
      setError(err?.response?.status === 401 ? "Incorrect password" : "Login failed. Try again.");
    } finally {
      setLoading(false);
    }
  };

  if (authed) return children;

  return (
    <div className="min-h-screen flex items-center justify-center px-6" style={{ background: P.bg, color: P.ink }} data-testid="ops-gate">
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true" style={{
        backgroundImage: `radial-gradient(circle at 30% 40%, ${P.green}12 0%, transparent 45%), radial-gradient(circle at 70% 60%, ${P.rose}0d 0%, transparent 45%)`,
      }} />

      <div className="relative w-full max-w-[420px]">
        <Link to="/" className="inline-flex items-center gap-2 text-[10.5px] uppercase tracking-[0.28em] mb-8" style={{ color: P.silver }} data-testid="ops-gate-back">
          <ChevronRight size={11} className="rotate-180" /> BSGOTECH
        </Link>

        <div className="rounded-xl p-8" style={{ background: P.bg2, border: `1px solid ${P.border}` }}>
          <div className="w-12 h-12 rounded-full flex items-center justify-center mb-5"
            style={{ background: `${P.green}18`, border: `1px solid ${P.green}55` }}>
            <Lock size={20} style={{ color: P.green }} strokeWidth={1.75} />
          </div>
          <div className="text-[10.5px] uppercase tracking-[0.32em] font-semibold" style={{ color: P.green }}>
            Operations Center
          </div>
          <h1 className="mt-2 font-semibold tracking-tight text-[24px] leading-tight" style={{ color: "#fff", letterSpacing: "-0.01em" }}>
            Restricted access.
          </h1>
          <p className="mt-2 text-[12.5px] leading-relaxed" style={{ color: P.dim }}>
            This area contains confidential partner documents, supplier data and the BS Assistente. Please enter the internal access password.
          </p>

          <form onSubmit={submit} className="mt-6">
            <label className="text-[10px] uppercase tracking-[0.28em] font-semibold" style={{ color: P.silver }}>Password</label>
            <input
              ref={inputRef}
              type="password"
              value={password}
              onChange={(e) => { setPassword(e.target.value); setError(""); }}
              autoFocus
              autoComplete="current-password"
              data-testid="ops-gate-password"
              className="mt-2 w-full rounded-md px-3.5 py-2.5 text-[13px] outline-none transition"
              style={{
                background: "rgba(8,11,18,0.6)",
                border: `1px solid ${error ? P.rose : P.border}`,
                color: P.ink,
              }}
              placeholder="Enter password"
            />
            {error && (
              <div className="mt-2 text-[11px]" style={{ color: P.rose }} data-testid="ops-gate-error">{error}</div>
            )}
            <button
              type="submit"
              disabled={loading || !password}
              data-testid="ops-gate-submit"
              className="mt-5 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 text-[12.5px] font-semibold rounded-md transition"
              style={{
                background: loading ? "rgba(255,255,255,0.08)" : "#fff",
                color: loading ? P.silver : "#0A0F1A",
                cursor: loading || !password ? "not-allowed" : "pointer",
                opacity: !password ? 0.5 : 1,
              }}
            >
              <LogIn size={13} />
              {loading ? "Verifying…" : "Enter Operations Center"}
            </button>
          </form>

          <div className="mt-6 pt-5 flex items-center gap-2 text-[10.5px]" style={{ color: P.silver, borderTop: `1px solid ${P.border}` }}>
            <ShieldCheck size={12} />
            <span>Internal access · session valid for 7 days</span>
          </div>
        </div>
      </div>
    </div>
  );
}
